import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Circle as CircleIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useThemeMode } from 'context/ThemeContext';
import { BglPrimaryButton, BglPrimaryOutlinedButton,BglCloseOutlinedSmallButton, BglSecondarySmallButton, BglPrimaryOutlinedButtonBase } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { ClusterApis } from 'apis/clusterAPI';
import { useToast } from 'context/ToastContext';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';
import { Shapes } from 'lucide-react';
import { KeyRound } from 'lucide-react';
import { Lock } from 'lucide-react';
import { CirclePlus } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { SquarePen } from 'lucide-react';
import { List,Folder,Component, Text ,CaseSensitive,Info } from 'lucide-react';


// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
}));

const FilterChip = styled(Chip)<{ selected?: boolean }>(({ theme, selected }) => ({
  borderRadius: 8,
  fontWeight: 500,
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.hover,
  },
}));

const StatusIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  borderRadius: 6,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

interface Cluster {
  id: string;
  name: string;
  reference: string;
  type: string;
  categories: string[];
  userCount: number | null;
  isActive: boolean;
}

export const DCXClustersPage: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  const selectedPilot = pilotList.find((pilot: any) => String(pilot.projectId) === String(selectedPilotId));
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(false);

  // Copy to clipboard function - memoized to prevent unnecessary re-renders
  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Cluster ID copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy Cluster ID');
    });
  }, [toast]);

  // Fetch clusters on component mount
  useEffect(() => {
    const fetchClusters = async () => {

      try {
        setLoading(true);
        const projectId = selectedPilotId.toString();
        const response = await ClusterApis.getClustersList(rc, projectId);
        setLoading(false);
        
        if (response.success && response.data) {
          // Helper function to normalize category names (convert to title case)
          const normalizeCategory = (cat: string): string => {
            if (!cat) return '';
            const upper = cat.toUpperCase();
            // Map common category values
            if (upper === 'PROGRAM') return 'Program';
            if (upper === 'FEATURE') return 'Feature';
            if (upper === 'TEMPLATE') return 'Template';
            if (upper === 'CONTENT') return 'Content';
            // Fallback: capitalize first letter, lowercase rest
            return cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
          };

          // Transform API response to match UI structure
          const transformedClusters: Cluster[] = response.data.map((cluster: any) => {
            // Normalize categories from API (they might be in uppercase)
            const rawCategories = cluster.cluster_categories || [];
            const normalizedCategories = rawCategories.map((cat: string) => normalizeCategory(cat));
            
            return {
              id: cluster.cluster_id || '',
              name: cluster.cluster_name || '',
              reference: cluster.cluster_reference || cluster.clusterReference || '',
              type: cluster.cluster_type === 'VIRTUAL' ? 'Virtual' : cluster.cluster_type === 'PHYSICAL' ? 'Physical' : cluster.cluster_type || '',
              categories: normalizedCategories,
              userCount: null, // API doesn't provide user count
              isActive: cluster.status?.toLowerCase() !== 'inactive'
            };
          });
          
          setClusters(transformedClusters);
        } else {
          toast.error(response.error || 'Failed to fetch clusters');
          setClusters([]);
        }
      } catch (error) {
        rc.error(rc.getName(DCXClustersPage), 'Error fetching clusters:', error);
        toast.error('Failed to fetch clusters');
        setClusters([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClusters();
  }, [rc, selectedPilotId]);

  // Calculate category counts - respect the showInactive filter
  const categoryCounts = React.useMemo(() => {
    // First, filter clusters based on active/inactive toggle
    const clustersToCount = showInactive 
      ? clusters 
      : clusters.filter(cluster => cluster.isActive);

    const counts: { [key: string]: number } = {
      All: clustersToCount.length,
      Program: 0,
      Feature: 0,
      Template: 0,
      Content: 0,
    };

    clustersToCount.forEach(cluster => {
      cluster.categories.forEach(category => {
        // Normalize category name to match the keys
        const normalizedCategory = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
        if (counts.hasOwnProperty(normalizedCategory)) {
          counts[normalizedCategory]++;
        }
      });
    });

    return counts;
  }, [clusters, showInactive]);

  const filteredClusters = clusters.filter(cluster => {
    const matchesSearch = (cluster.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cluster.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Case-insensitive category matching
    const matchesCategory = selectedCategory === 'All' || 
      cluster.categories.some(cat => 
        cat.toLowerCase() === selectedCategory.toLowerCase()
      );
    
    // By default, only show active clusters. When toggle is enabled, show all (active + inactive)
    const matchesActiveFilter = showInactive ? true : cluster.isActive;
    
    return matchesSearch && matchesCategory && matchesActiveFilter;
  });

  // Shared dark blue color for Virtual type and Feature category
  const getDarkBlueColor = () => {
    return mode === 'dark' ? '#1976d2' : '#1565c0';
  };

  const getTypeColor = (type: string) => {
    // Physical = teal (primary), Virtual = dark blue
    if (type === 'Physical') {
      return theme.palette.primary.main;
    }
    // Virtual = dark blue (same as Feature)
    return theme.palette.secondary.dark
  };

  const getCategoryColor = (category: string) => {
    const normalizedCategory = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
    const darkBlue = getDarkBlueColor();
    const colors: { [key: string]: string } = {
      Program: theme.palette.primary.main, // Teal
      Feature: theme.palette.secondary.dark, // Dark blue (same as Virtual)
      Template: mode === 'dark' ? '#42a5f5' : '#2196f3', // Blue (lighter than Feature)
      Content: theme.palette.secondary.main, // Dark blue (same as Virtual and Feature)
    };
    return colors[normalizedCategory] || theme.palette.grey[500];
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
    }}>
      {/* Configuration Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Shapes size={18} style={{ marginRight: '8px', color: 'primary.main' }} />
                <Typography variant="subtitle1">
                  Cluster Configuration
                </Typography>
              </Box>
              <Typography variant="body2"  sx={{ mb: 3,textAlign:"left",fontSize:"12px", height:'30px' }}>
                Define and manage Dynamic Customer Experience (DCX) clusters.
              </Typography>
              <Button
                variant='outlined'
                startIcon={<CirclePlus size={16}/>}
                fullWidth
                sx={{ borderRadius: '6px',minHeight: '34px',
                        color: 'text.secondary',
                        borderColor: 'divider'
                }}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS_CREATE)}
              >
                Create Cluster
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <KeyRound size={18} style={{ marginRight: '8px', color: 'primary.main' }} />
                <Typography variant="subtitle1" >
                  Feature Access
                </Typography>
              </Box>
              <Typography variant="body2"  sx={{ mb: 3,textAlign:"left",fontSize:"12px" ,height:'30px' }}>
                Manage feature access based on 'Feature' clusters.
              </Typography>
              <Button
                variant='outlined'
                endIcon={<ArrowRight size={16} />}
                fullWidth
                sx={{ borderRadius: '6px', minHeight:'34px',color: 'text.secondary',
                    borderColor: 'divider'}}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.FEATURE_ACCESS)}
              >
                Configure
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Lock size={18} style={{ marginRight: '8px', color: 'primary.main' }} />
                <Typography variant="subtitle1">
                  Template Personalization
                </Typography>
              </Box>
              <Typography variant="body2"  sx={{ mb: 3,textAlign:"left" ,fontSize:"12px" ,height:'30px' }}>
                Apply template logic based on 'Template' clusters.
              </Typography>
              <Button
                variant='outlined'
                endIcon={<ArrowRight size={16}/>}
                fullWidth
                sx={{ borderRadius: '6px', minHeight:'34px' ,color: 'text.secondary',
                    borderColor: 'divider'}}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.TEMPLATE_PERSONALIZATION)}
              >
                Configure
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Cluster List Section */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',textAlign:"left" }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Cluster List
            </Typography>
            <Typography variant="body2"  sx={{fontSize:"12px" ,height:'30px' }}>
              A list of all defined clusters. Use the category boxes to filter the list below.
            </Typography>
          </Box>

          {/* Filter Categories */}
          <Box sx={{ mb: 3}}>
            <Stack direction="row" spacing={1} useFlexGap sx={{justifyContent:'space-between' }}>
              {Object.entries(categoryCounts).map(([category, count]: [string, number]) => {
                const getCategoryIcon = (cat: string) => {
                  switch (cat) {
                    case 'All': return <List />;
                    case 'Program': return <Folder />;
                    case 'Feature': return <Component />;
                    case 'Template': return <Text />;
                    case 'Content': return <CaseSensitive />;
                    default: return <CircleIcon />;
                  }
                };

                return (
                  <Box key={category} sx={{ position: 'relative',width: '-webkit-fill-available'}}>

                    <Box 
                      onClick={() => setSelectedCategory(category)}
                      style={{
                        display:'flex',
                        flexDirection:'column',
                        alignItems:'center',
                        gap:8,
                        borderRadius:8,
                        padding:'12px',
                        border: selectedCategory === category ? `2px solid ${theme.palette.primary.main}` : '1px solid #E0E0E0',
                        height:'100px',
                        width:'100%',
                        cursor: 'pointer',
                        backgroundColor: selectedCategory === category ? theme.palette.primary.main + '10' : 'transparent',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedCategory !== category) {
                          e.currentTarget.style.borderColor = theme.palette.primary.main;
                          e.currentTarget.style.backgroundColor = theme.palette.primary.main + '05';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedCategory !== category) {
                          e.currentTarget.style.borderColor = '#E0E0E0';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Box style={{display:'flex',alignItems:'center',gap:1,justifyContent:'space-between',width:'100%'}}>
                      {getCategoryIcon(category)}
                      <Tooltip title={`Filter by ${category} clusters`} arrow placement="top">
                        {/* <IconButton
                          size="small"
                          sx={{
                            // width: 16,
                            // height: 16,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            }
                          }}
                        > */}
                          <Info size={16} style={{ color: theme.palette.text.secondary }} />
                        {/* </IconButton> */}
                      </Tooltip>
                      </Box>
                      <Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '14px', 
                            fontWeight: 600,
                            textAlign:'center',
                            color: selectedCategory === category ? theme.palette.primary.main : 'inherit'
                          }}
                        >
                          {`${category}`}
                        </Typography>
                        <Typography variant="body2"  sx={{ 
                          fontSize: '12px', 
                          fontWeight: 500,
                          textAlign:'center',
                          color: selectedCategory === category ? theme.palette.primary.main : 'inherit'
                        }}>
                          {`(${count})`}
                        </Typography>
                      </Box>
                    </Box>
                    
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Search and Toggle */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            mb: 3,
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <TextField
              placeholder="Search clusters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ width:'100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                  sx={{
                    width: 38,
                    height: 22,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                      padding: 0,
                      margin: '2px',
                      transitionDuration: '300ms',
                      '&.Mui-checked': {
                        transform: 'translateX(16px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: theme.palette.primary.main,
                          opacity: 1,
                          border: 0,
                        },
                        '& .MuiSwitch-thumb': {
                          color: '#fff',
                        },
                      },
                      '&.Mui-focusVisible .MuiSwitch-thumb': {
                        color: theme.palette.primary.main,
                        border: '6px solid #fff',
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      boxSizing: 'border-box',
                      width: 18,
                      height: 18,
                      color: '#fff',
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 22 / 2,
                      backgroundColor: mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                      opacity: 1,
                      transition: theme.transitions.create(['background-color'], {
                        duration: 500,
                      }),
                    },
                  }}
                />
              }
              label="Show inactive clusters"
              sx={{ 
                '& .MuiFormControlLabel-label': { 
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  marginLeft: 1
                } 
              }}
            />
          </Box>

          {/* Cluster Table */}
          {loading ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8, 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: 'background.default'
            }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loading clusters...
              </Typography>
            </Box>
          ) : filteredClusters.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Cluster Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Categories</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>User Count</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClusters.map((cluster) => {
                    const isInactive = !cluster.isActive;
                    const isDark = mode === 'dark';
                    
                    // Better visibility for inactive clusters in dark mode
                    const inactiveTextColor = isDark 
                      ? theme.palette.text.secondary 
                      : theme.palette.text.disabled;
                    
                    const inactiveChipBg = isDark 
                      ? theme.palette.grey[700] 
                      : theme.palette.grey[400];
                    
                    const inactiveChipColor = isDark 
                      ? theme.palette.text.secondary 
                      : 'white';
                    
                    const textColor = isInactive ? inactiveTextColor : 'inherit';
                    const chipBg = isInactive ? inactiveChipBg : (cluster.type === 'Physical' ? theme.palette.primary.main : theme.palette.grey[500]);
                    const chipColor = isInactive ? inactiveChipColor : 'white';
                    
                    return (
                      <TableRow 
                        key={cluster.id} 
                        hover
                        sx={{
                          opacity: isInactive ? (isDark ? 0.7 : 0.5) : 1,
                          '&:hover': {
                            opacity: isInactive ? (isDark ? 0.85 : 0.7) : 1,
                          }
                        }}
                      >
                        <TableCell>
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  Cluster ID: {cluster.id}
                                </Typography>
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5, 
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 }
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToClipboard(cluster.id);
                                  }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                                  <Typography variant="caption">Click to copy</Typography>
                                </Box>
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: textColor }}>
                                {cluster.reference || 'N/A'}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  copyToClipboard(cluster.id);
                                }}
                                sx={{ 
                                  color: textColor,
                                  opacity: 0.7,
                                  '&:hover': { opacity: 1 },
                                  p: 0.5
                                }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cluster.type}
                            size="small"
                            sx={{
                              bgcolor: isInactive ? inactiveChipBg : getTypeColor(cluster.type),
                              color: chipColor,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {cluster.categories.map((category) => (
                              <Chip
                                key={category}
                                label={category}
                                size="small"
                                sx={{
                                  bgcolor: isInactive ? inactiveChipBg : getCategoryColor(category),
                                  color: chipColor,
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: textColor }}>
                            {cluster.userCount ? cluster.userCount.toLocaleString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit cluster">
                            <IconButton 
                              size="small" 
                              sx={{ color: textColor }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Navigate to create page with cluster ID for editing
                                rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS_CREATE, {
                                  state: { clusterId: cluster.id }
                                });
                              }}
                            >
                              <SquarePen size={16} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8, 
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: 'background.default'
            }}>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                No clusters found
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                {searchTerm ? 'Try adjusting your search criteria' : 'No clusters match the selected category'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS_CREATE)}
                sx={{ textTransform: 'none' }}
              >
                Create First Cluster
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
